import type { AdminPermissionString, AdminRbacRole } from '@/types/admin'

/**
 * 后台 RBAC 常量配置
 * - 统一定义 admin:*:* 权限点
 * - 定义预置后台角色与权限映射
 */

export const ADMIN_RBAC_PERMISSIONS = {
  DASHBOARD_VIEW: 'admin:dashboard:view',

  MODERATION_TASK_VIEW: 'admin:moderation:task:view',
  MODERATION_TASK_AUDIT: 'admin:moderation:task:audit',
  MODERATION_MODEL_VIEW: 'admin:moderation:model:view',
  MODERATION_MODEL_AUDIT: 'admin:moderation:model:audit',
  MODERATION_BID_VIEW: 'admin:moderation:bid:view',
  MODERATION_BID_AUDIT: 'admin:moderation:bid:audit',
  MODERATION_DESIGNER_VIEW: 'admin:moderation:designer:view',
  MODERATION_DESIGNER_AUDIT: 'admin:moderation:designer:audit',
  MODERATION_PROVIDER_VIEW: 'admin:moderation:provider:view',
  MODERATION_PROVIDER_AUDIT: 'admin:moderation:provider:audit',
  MODERATION_DESIGNER_PROJECT_VIEW: 'admin:moderation:designer-project:view',
  MODERATION_DESIGNER_PROJECT_AUDIT: 'admin:moderation:designer-project:audit',
  MODERATION_PROVIDER_PROJECT_VIEW: 'admin:moderation:provider-project:view',
  MODERATION_PROVIDER_PROJECT_AUDIT: 'admin:moderation:provider-project:audit',

  // 兼容 auth.ts 中的通用项目权限
  MODERATION_PROJECT_VIEW: 'admin:moderation:project:view',
  MODERATION_PROJECT_AUDIT: 'admin:moderation:project:audit',

  FINANCE_WITHDRAW_VIEW: 'admin:finance:withdraw:view',
  FINANCE_WITHDRAW_AUDIT: 'admin:finance:withdraw:audit',

  COMPLAINT_VIEW: 'admin:complaint:view',

  OPS_DASHBOARD_VIEW: 'admin:ops:dashboard:view',
  OPS_MONITOR_VIEW: 'admin:ops:monitor:view',

  SYSTEM_SETTINGS_VIEW: 'admin:system:settings:view',
  SYSTEM_CLIENT_USERS_VIEW: 'admin:system:client-users:view',
  SYSTEM_CLIENT_USERS_MANAGE: 'admin:system:client-users:manage',

  SYSTEM_ADMINS_VIEW: 'admin:system:admins:view',
  SYSTEM_ADMINS_MANAGE: 'admin:system:admins:manage',
  SYSTEM_ADMINS_RESET_PASSWORD: 'admin:system:admins:reset-password',

  SYSTEM_PERMISSION_LOGS_VIEW: 'admin:system:permission-logs:view',

  // 基础数据管理
  BASE_DATA_VIEW: 'admin:base-data:view',
  BASE_DATA_EDIT: 'admin:base-data:edit',

  // V4新增：内容管控权限
  CONTENT_VIEW: 'admin:content:view',
  CONTENT_MODEL_TAKEDOWN: 'admin:content:model:takedown',
  CONTENT_TASK_CLOSE: 'admin:content:task:close',
  CONTENT_RESTORE: 'admin:content:restore',

  // V4新增：用户处罚权限
  USER_PUNISH: 'admin:user:punish',
  USER_REVOKE_PUNISHMENT: 'admin:user:revoke-punishment',
} as const

export type AdminPermission =
  (typeof ADMIN_RBAC_PERMISSIONS)[keyof typeof ADMIN_RBAC_PERMISSIONS]

/**
 * 预置后台角色 → 权限映射
 * 说明：
 * - SUPER_ADMIN 拥有所有权限点
 * - 其他角色按职责拥有子集
 */

const P = ADMIN_RBAC_PERMISSIONS

export const ADMIN_ROLE_PERMISSIONS: Record<AdminRbacRole, AdminPermissionString[]> = {
  reviewer: [
    P.DASHBOARD_VIEW,
    P.MODERATION_TASK_VIEW,
    P.MODERATION_TASK_AUDIT,
    P.MODERATION_MODEL_VIEW,
    P.MODERATION_MODEL_AUDIT,
    P.MODERATION_BID_VIEW,
    P.MODERATION_BID_AUDIT,
    P.MODERATION_DESIGNER_VIEW,
    P.MODERATION_DESIGNER_AUDIT,
    P.MODERATION_PROVIDER_VIEW,
    P.MODERATION_PROVIDER_AUDIT,
    P.MODERATION_DESIGNER_PROJECT_VIEW,
    P.MODERATION_DESIGNER_PROJECT_AUDIT,
    P.MODERATION_PROVIDER_PROJECT_VIEW,
    P.MODERATION_PROVIDER_PROJECT_AUDIT,
    P.COMPLAINT_VIEW,
  ],
  finance: [
    P.DASHBOARD_VIEW,
    P.FINANCE_WITHDRAW_VIEW,
    P.FINANCE_WITHDRAW_AUDIT,
    P.OPS_DASHBOARD_VIEW,
  ],
  admin: [
    P.DASHBOARD_VIEW,
    P.MODERATION_TASK_VIEW,
    P.MODERATION_TASK_AUDIT,
    P.MODERATION_MODEL_VIEW,
    P.MODERATION_MODEL_AUDIT,
    P.MODERATION_BID_VIEW,
    P.MODERATION_BID_AUDIT,
    P.MODERATION_DESIGNER_VIEW,
    P.MODERATION_DESIGNER_AUDIT,
    P.MODERATION_PROVIDER_VIEW,
    P.MODERATION_PROVIDER_AUDIT,
    P.MODERATION_DESIGNER_PROJECT_VIEW,
    P.MODERATION_DESIGNER_PROJECT_AUDIT,
    P.MODERATION_PROVIDER_PROJECT_VIEW,
    P.MODERATION_PROVIDER_PROJECT_AUDIT,
    P.COMPLAINT_VIEW,
    P.FINANCE_WITHDRAW_VIEW,
    P.FINANCE_WITHDRAW_AUDIT,
    P.OPS_DASHBOARD_VIEW,
    P.OPS_MONITOR_VIEW,
    P.SYSTEM_SETTINGS_VIEW,
    P.SYSTEM_CLIENT_USERS_VIEW,
    P.SYSTEM_CLIENT_USERS_MANAGE,
    P.SYSTEM_ADMINS_VIEW,
    P.SYSTEM_ADMINS_MANAGE,
    P.SYSTEM_ADMINS_RESET_PASSWORD,
    P.BASE_DATA_VIEW,
    P.BASE_DATA_EDIT,
    // V4新增权限
    P.CONTENT_VIEW,
    P.CONTENT_MODEL_TAKEDOWN,
    P.CONTENT_TASK_CLOSE,
    P.USER_PUNISH,
  ],
  super_admin: Object.values(ADMIN_RBAC_PERMISSIONS),
}
